import React from 'react';
import { Shield } from 'lucide-react';
import { LEGAL_EFFECTIVE_DATE_DISPLAY } from '../utils/legalVersion';

// Legal content reflecting what QuoteHaul actually does today.
export default function PrivacyPolicyPage({ onOpenTerms }) {
  return (
    <div className="space-y-5 text-sm text-slate-300 leading-relaxed">
      <div className="flex items-center gap-2.5">
        <Shield className="w-5 h-5 text-blue-400 shrink-0" />
        <h1 className="text-xl font-bold text-white">Privacy Policy</h1>
      </div>
      <p className="text-xs text-slate-500">Last updated: {LEGAL_EFFECTIVE_DATE_DISPLAY}</p>

      <section className="space-y-2">
        <h2 className="text-white font-bold">1. What This Policy Covers</h2>
        <p>
          This Privacy Policy describes how XAYVR Company, LLC, a Georgia limited liability company ("QuoteHaul,"
          "we," "us") collects, uses, and stores information when you use QuoteHaul, a freight quoting and
          shipment management application. See our{' '}
          <button type="button" onClick={() => onOpenTerms && onOpenTerms()} className="text-blue-400 hover:underline font-semibold">
            Terms of Service
          </button>
          {' '}for the terms governing your use of the Service.
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="text-white font-bold">2. Information We Collect</h2>
        <p><strong className="text-slate-200">Account &amp; company information</strong> — collected when you create an account: your name, email address, phone number (optional), and your company's name, address, phone, and website.</p>
        <p><strong className="text-slate-200">Customer Data you create</strong> — quotes, shipment and BOL/invoice records, organization/contact directory entries, rate cards, and any documents you upload (e.g. BOLs, PODs), all associated with your organization.</p>
        <p><strong className="text-slate-200">Billing information</strong> — subscription tier and payment status. Card details themselves are collected and stored directly by Stripe, our payment processor — QuoteHaul does not receive or store your full card number.</p>
        <p><strong className="text-slate-200">Local browser storage</strong> — if you use QuoteHaul without creating an account ("guest mode"), quotes and organization records are stored only in your browser's local storage, not on our servers, and are not accessible to anyone else.</p>
      </section>

      <section className="space-y-2">
        <h2 className="text-white font-bold">3. How We Use Information</h2>
        <ul className="list-disc list-inside space-y-1 pl-2">
          <li>To provide, maintain, and secure the Service, including tenant/organization data isolation.</li>
          <li>To process payments and manage subscriptions via Stripe.</li>
          <li>To send account-related communications (e.g. invitations, password resets, billing notices).</li>
          <li>To generate documents you request (quotes, BOLs, invoices) and, if you choose, email them to your customers/carriers on your behalf.</li>
        </ul>
        <p>We do not sell your personal information or Customer Data to third parties.</p>
      </section>

      <section className="space-y-2">
        <h2 className="text-white font-bold">4. Where Data Is Stored</h2>
        <p>
          Account and Customer Data are stored using Google Firebase (Firestore database, Firebase Authentication,
          and Firebase Storage for uploaded documents). Access is restricted by server-side security rules so that
          one organization's data is not visible to another. Payment data is stored by Stripe under its own
          privacy policy.
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="text-white font-bold">5. Data Sharing</h2>
        <p>We share information only with:</p>
        <ul className="list-disc list-inside space-y-1 pl-2">
          <li><strong className="text-slate-200">Stripe</strong>, to process payments and manage subscriptions.</li>
          <li><strong className="text-slate-200">Google Firebase / Google Cloud</strong>, as our infrastructure and hosting provider.</li>
          <li>Other members of your own organization, according to the roles/permissions your organization's admin assigns.</li>
          <li>Third parties you explicitly direct us to send documents to (e.g. emailing a BOL to a carrier or an invoice to a customer you specify).</li>
        </ul>
        <p>We do not otherwise share personal information or Customer Data with third parties, except where required by law.</p>
      </section>

      <section className="space-y-2">
        <h2 className="text-white font-bold">6. Analytics and Cookies</h2>
        <p>
          Our public marketing site (quotehaul.app) uses Google Analytics (via Firebase Analytics) to understand
          how visitors find and use the site — for example, which page led someone to start a trial, or whether a
          checkout was started. This sets standard analytics cookies/identifiers in your browser and is enabled by
          default. It does not track you across other, unrelated websites, and we do not use it to build advertising
          profiles or sell data derived from it. You can control cookies generally through your browser's own
          settings; blocking them will not prevent you from using QuoteHaul, only from being counted in this
          analytics data.
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="text-white font-bold">7. Data Retention and Deletion</h2>
        <p>
          We retain account and Customer Data for as long as your account is active, so your organization's
          quote/shipment history remains available to you. To request deletion of your organization's account and
          data, contact us (Section 10) — we'll process deletion requests as required by applicable law.
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="text-white font-bold">8. Your Choices</h2>
        <p>
          You can review and update your profile and company information at any time from Settings. An
          organization admin can revoke a teammate's access at any time. You may request a copy of, or the
          deletion of, your personal information by contacting us.
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="text-white font-bold">9. Children's Privacy</h2>
        <p>QuoteHaul is a business tool not directed at children, and we do not knowingly collect information from anyone under 18.</p>
      </section>

      <section className="space-y-2">
        <h2 className="text-white font-bold">10. Contact</h2>
        <p>
          Questions about this Privacy Policy, or requests to access/delete your data, can be sent to{' '}
          <a href="mailto:support@quotehaul.app" className="text-blue-400 hover:underline">support@quotehaul.app</a>.
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="text-white font-bold">11. Changes to This Policy</h2>
        <p>
          If we make material changes to this policy, we'll update the "Last updated" date above. Continued use
          of QuoteHaul after a change constitutes acceptance of the updated policy.
        </p>
      </section>
    </div>
  );
}
